import type {
  DocumentSchema,
  SearchResponse,
} from 'typesense/lib/Typesense/Documents';
import type { SetlistContext } from './client';
import { STOCK_VIDEO_COLLECTION } from './collections';
import { mergeSearchResponses } from './get-stock-footage-by-category';
import {
  composeStockVideoSort,
  DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  STOCK_VIDEO_INCLUDE_FIELDS,
  VIDEO_ENGAGEMENT_SORT,
  VIDEO_RECENCY_TIE_BREAKER,
  withEngagementFilter,
} from './stock-video-engagement-join';

export interface ZoneSampledParams {
  filterBy?: string;
  isSubscribedUser?: boolean;
  page?: number;
  perPage?: number;
  slug?: string;
}

const searchWithRelevanceSort = async (
  context: SetlistContext,
  {
    accountTypeGroup,
    page,
    perPage,
    filterBy,
    premiumFilter,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  }: {
    accountTypeGroup: 'paid' | 'free';
    page: number;
    perPage: number;
    filterBy?: string;
    premiumFilter?: string;
    slug?: string;
  }
): Promise<SearchResponse<DocumentSchema>> => {
  const effectiveFilter = [premiumFilter, filterBy]
    .filter(Boolean)
    .join(' && ');

  const engagementFilter = withEngagementFilter(
    accountTypeGroup,
    effectiveFilter || undefined,
    slug
  );

  const response = await context.client
    .collections(STOCK_VIDEO_COLLECTION)
    .documents()
    .search(
      {
        q: '*',
        page,
        per_page: perPage,
        include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
        filter_by: engagementFilter,
        sort_by: composeStockVideoSort([
          VIDEO_ENGAGEMENT_SORT,
          VIDEO_RECENCY_TIE_BREAKER,
        ]),
      },
      { cacheSearchResultsForSeconds: 120 }
    );

  return response as SearchResponse<DocumentSchema>;
};

/**
 * Stock footage search for the homepage, sorted by general relevance —
 * engagement score with a recency tie-breaker, matching the ordering used
 * across other stock-video pages (DEV-6304 replaced the previous
 * session-seeded `_rand()` sampling with this stable ordering).
 *
 * For free/logged-out users, makes two parallel queries (premium + free)
 * and merges proportionally using `mergeSearchResponses`.
 */
export const getStockFootageZoneSampled = async (
  context: SetlistContext,
  {
    page = 1,
    perPage = 24,
    filterBy,
    isSubscribedUser = false,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  }: ZoneSampledParams = {}
): Promise<SearchResponse<DocumentSchema>> => {
  if (isSubscribedUser) {
    return searchWithRelevanceSort(context, {
      accountTypeGroup: 'paid',
      page,
      perPage,
      filterBy,
      slug,
    });
  }

  // Free / logged-out: split premium + free, merge proportionally.
  const [premiumResults, freeResults] = await Promise.all([
    searchWithRelevanceSort(context, {
      accountTypeGroup: 'paid',
      page,
      perPage,
      filterBy,
      premiumFilter: 'is_premium:=true',
      slug,
    }),
    searchWithRelevanceSort(context, {
      accountTypeGroup: 'free',
      page,
      perPage,
      filterBy,
      premiumFilter: 'is_premium:=false',
      slug,
    }),
  ]);

  return mergeSearchResponses(premiumResults, freeResults, perPage);
};
